import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '../../components/Button';
import { Dropdown } from '../../components/Dropdown';
import ImageUploader from '../../components/ImageUploader';
import Table, { Column, TableHandle } from '../../components/Table';
import Modal from '../../components/Modal';
import { apiGet, apiPost } from '../../services/apiServices';
import { endPoints } from '../../configs/apiUrls';
import { toast } from 'react-toastify';
import moment from 'moment';
import indiaData from '../../data/stateData.json';

const targetOptions = [
  { label: "All Users", value: "all" },
  { label: "By Role (Agents, Valuers, etc.)", value: "role" },
  { label: "By Location (State, City, Area)", value: "location" }
];

const roleOptions = [
  { label: "Customers / Normal Users", value: "user" },
  { label: "Real Estate Agents", value: "agent" },
  { label: "Valuers / Technical Experts", value: "valuer" }
];

const SendNotification = () => {
  const tableRef = useRef<TableHandle>(null);

  // --- Table States ---
  const [notificationsList, setNotificationsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);
  const [filterData, setFilterData] = useState({ page: 1, limit: 10, search: "" });

  // --- Modal & Form States ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState<File | null>(null);
  
  const [targetType, setTargetType] = useState("all");
  const [selectedRole, setSelectedRole] = useState("");
  
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [areaOptions, setAreaOptions] = useState<any[]>([]);

  // --- Fetch Notifications History ---
  const fetchNotifications = async () => {
    setLoadingTable(true);
    try {
      const params = new URLSearchParams({
        page: filterData.page.toString(),
        limit: filterData.limit.toString(),
        ...(filterData.search && { search: filterData.search })
      });

      // 🚨 Ensure you have GET_NOTIFICATIONS defined in your apiUrls.ts
      const res: any = await apiGet(`${endPoints.GET_NOTIFICATIONS}?${params.toString()}`);
      if (res.success) {
        setNotificationsList(res.data);
        setTotalCount(res.total);
      }
    } catch (error) {
      toast.error("Failed to fetch notifications");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filterData.page, filterData.limit, filterData.search]);

  // --- Reset dependent fields when target type changes ---
  useEffect(() => {
    setSelectedRole("");
    setState("");
    setCity("");
    setArea("");
    setAreaOptions([]);
  }, [targetType]);

  // --- Form Reset ---
  const resetForm = () => {
    setTitle("");
    setMessage("");
    setImage(null);
    setTargetType("all");
    setSelectedRole("");
    setState("");
    setCity("");
    setArea("");
  };

  // --- Location Fetchers ---
  const fetchAreas = async (cityName: string) => {
    try {
      const res: any = await apiGet(`${endPoints.GET_AREA_LIST_BY_CITY}/${cityName}`);
      if (res?.success) {
        const formatted = res.data.map((item: any) => ({
          label: item.label || item.area || item.areaName,
          value: item.value || item._id
        }));
        setAreaOptions(formatted);
      }
    } catch (error) {
      console.error("Failed to fetch areas", error);
    }
  };

  const onStateChange = (val: string) => {
    setState(val);
    setCity("");
    setArea("");
    setAreaOptions([]);
  };

  const onCityChange = (val: string) => {
    setCity(val);
    setArea("");
    if (val) fetchAreas(val);
    else setAreaOptions([]);
  };

  const stateOptions = useMemo(() => indiaData.map((s: any) => ({ label: s.state, value: s.state })), []);
  const cityOptions = useMemo(() => {
    if (!state) return [];
    const stateObj = indiaData.find((s: any) => s.state === state);
    return stateObj ? stateObj.districts.map((d: string) => ({ label: d, value: d })) : [];
  }, [state]);

  // --- Submit Handler ---
  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      return toast.warning("Title and Message are required");
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("message", message);
      formData.append("targetType", targetType);

      if (targetType === "role" && selectedRole) {
        formData.append("role", selectedRole);
      }

      if (targetType === "location") {
        if (state) formData.append("state", state);
        if (city) formData.append("city", city);
        if (area) formData.append("area", area);
      }

      if (image) {
        formData.append("image", image); 
      }

      // Note: Removed explicit Content-Type headers for FormData to let the browser set boundaries
      const res: any = await apiPost(endPoints.SEND_NOTIFICATION, formData); 
      
      if (res.success) {
        toast.success(`Notification sent successfully to ${res.successCount || 'eligible'} devices!`);
        setIsModalOpen(false);
        resetForm();
        fetchNotifications(); // Refresh the table
      } else {
        toast.error(res.message || "Failed to send notification");
      }
    } catch (error) {
      toast.error("Error sending notification");
    } finally {
      setLoading(false);
    }
  };

  // --- Table Columns ---
  const columns: Column<any>[] = [
    {
      key: "createdAt",
      label: "Date Sent",
      customCell: (row) => (
        <span className='text-sm text-gray-600'>{moment(row.createdAt).format("DD MMM YYYY, hh:mm A")}</span>
      )
    },
    {
      key: "imageUrl",
      label: "Image",
      sortable: false,
      customCell: (row) => (
        row.imageUrl ? (
          <img src={row.imageUrl} alt="Notification" className="h-12 w-12 object-cover rounded-md border" />
        ) : (
          <span className="text-gray-400 text-xs italic">No Image</span>
        )
      )
    },
    {
      key: "title",
      label: "Title & Message",
      customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span className="font-semibold text-gray-800">{row.title}</span>
          <span className="text-gray-500 line-clamp-2 max-w-xs">{row.message}</span>
        </div>
      )
    },
    {
      key: "targetType",
      label: "Target Audience",
      customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span className="font-medium capitalize text-blue-700">{row.targetType}</span>
          {row.targetType === "role" && <span className="text-xs text-gray-500">Role: {row.role}</span>}
          {row.targetType === "location" && (
            <span className="text-xs text-gray-500">
              {row.state} {row.city ? `, ${row.city}` : ""} {row.area ? `(${row.area})` : ""}
            </span>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="p-2">

      {/* TABLE */}
      <Table
        ref={tableRef}
        columns={columns}
        data={notificationsList?.length ? notificationsList : []}
        tableName="Notifications"
        totalCount={totalCount}
        currentPage={filterData.page}
        pageSize={filterData.limit}
        loading={loadingTable}
        onPageChange={(page) => setFilterData({ ...filterData, page })}
        onPageSizeChange={(size) => setFilterData({ ...filterData, limit: size, page: 1 })}
        onSearchChange={(searchText) => {
          setFilterData({ ...filterData, search: searchText, page: 1 });
        }}
        renderRightToolItems={
          <Button 
          variant="primary" 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
        >
          + Send New Notification
        </Button>
        }
      />

      {/* MODAL FORM */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Send Push Notification"
      >
        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {/* --- NOTIFICATION CONTENT --- */}
          <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">1. Message Content</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notification Title *</label>
            <input
              type="text"
              placeholder="e.g. Special Offer! / New Request Assigned"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Message Body *</label>
            <textarea
              rows={3}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notification Image (Optional)</label>
            <div className="mt-2">
              <ImageUploader
                value={image}
                onImageSelected={(file) => setImage(file)}
                onImageClear={() => setImage(null)}
                width={200}
                height={120}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Recommended ratio: 2:1 (e.g., 800x400px).</p>
          </div>

          {/* --- TARGETING RULES --- */}
          <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">2. Target Audience</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Who should receive this?</label>
            <Dropdown
              options={targetOptions}
              value={targetType}
              onChange={(val) => setTargetType(val as string)}
              placeholder="Select Target"
            />
          </div>

          {targetType === "role" && (
            <div className="mb-4 bg-blue-50 p-4 rounded border border-blue-100">
              <label className="block text-sm font-medium text-blue-800 mb-1">Select User Role</label>
              <Dropdown
                options={roleOptions}
                value={selectedRole}
                onChange={(val) => setSelectedRole(val as string)}
                placeholder="Select Role"
              />
            </div>
          )}

          {targetType === "location" && (
            <div className="mb-4 space-y-3 bg-green-50 p-4 rounded border border-green-100">
              <div>
                <label className="block text-sm font-medium text-green-800 mb-1">State *</label>
                <Dropdown
                  options={stateOptions}
                  value={state}
                  onChange={(val) => onStateChange(val as string)}
                  placeholder="Select State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-green-800 mb-1">City (Optional)</label>
                <Dropdown
                  options={cityOptions}
                  value={city}
                  onChange={(val) => onCityChange(val as string)}
                  placeholder={!state ? "Select State First" : "Select City"}
                  disabled={!state}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-green-800 mb-1">Area (Optional)</label>
                <Dropdown
                  options={areaOptions}
                  value={area}
                  onChange={(val) => setArea(val as string)}
                  placeholder={!city ? "Select City First" : "Select Area"}
                  disabled={!city}
                />
              </div>
            </div>
          )}

          {/* --- SUBMIT BUTTON --- */}
          <div className="mt-8 flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary"
              onClick={handleSendNotification} 
              disabled={loading}
            >
              {loading ? "Sending..." : "🚀 Send Notification"}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default SendNotification;